import React, { useState, useEffect } from 'react';
import { X, Laptop, User, Check, Loader2, Sparkles } from 'lucide-react';
import { api } from '../lib/api';
import { haptics } from '../lib/haptics';
import { sounds } from '../lib/sounds';

interface EditDeviceModalProps {
  isOpen: boolean;
  onClose: () => void;
  device: {
    id: string;
    deviceId: string;
    hostname: string;
    deviceName?: string;
    userId?: string | null;
    user?: {
      id: string;
      fullName: string;
      email?: string;
    } | null;
  } | null;
  onSaved: (updatedDevice: any) => void;
}

export function EditDeviceModal({ isOpen, onClose, device, onSaved }: EditDeviceModalProps) {
  const [deviceName, setDeviceName] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [users, setUsers] = useState<any[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && device) {
      setDeviceName(device.deviceName || device.hostname || '');
      setSelectedUserId(device.userId || device.user?.id || '');
      setError(null);

      // Load organization users for the staff dropdown
      setLoadingUsers(true);
      api.admin
        .getUsers()
        .then((res) => {
          setUsers(res.users || []);
        })
        .catch((err) => {
          console.error('Failed to load users:', err);
        })
        .finally(() => {
          setLoadingUsers(false);
        });
    }
  }, [isOpen, device]);

  if (!isOpen || !device) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!deviceName.trim()) {
      setError('Please provide a valid workstation name');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      haptics.impact();

      const res = await api.admin.updateDevice(device.id, {
        deviceName: deviceName.trim(),
        userId: selectedUserId ? selectedUserId : null,
      });

      sounds.playChimeSound();
      haptics.notificationSuccess();
      onSaved(res.device);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to update device settings');
      haptics.alert();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 select-none animate-ios-enter" role="dialog">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
        onClick={() => {
          sounds.playTapSound();
          onClose();
        }}
      />

      {/* Modal Surface */}
      <div className="relative w-full max-w-md rounded-3xl bg-white dark:bg-zinc-900 border border-black/[0.08] dark:border-white/10 shadow-2xl p-6 z-10 text-zinc-900 dark:text-white">
        <div className="flex items-center justify-between gap-3 border-b border-black/[0.06] dark:border-white/[0.08] pb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-pulse-blue/10 border border-pulse-blue/20 text-pulse-blue flex items-center justify-center shadow-sm">
              <Laptop className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-bold tracking-tight">Workstation Settings</h3>
              <p className="text-xs text-zinc-500 dark:text-zinc-400">Rename or assign staff member</p>
            </div>
          </div>

          <button
            onClick={() => {
              sounds.playTapSound();
              onClose();
            }}
            className="w-8 h-8 rounded-full bg-black/5 dark:bg-zinc-800 text-zinc-500 hover:text-black dark:hover:text-white flex items-center justify-center border border-black/5 dark:border-white/10 ios-press"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {error && (
          <div className="mt-4 p-3 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-600 dark:text-rose-400 text-xs">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-zinc-700 dark:text-zinc-300 mb-1.5">
              Workstation Display Name
            </label>
            <input
              type="text"
              value={deviceName}
              onChange={(e) => setDeviceName(e.target.value)}
              placeholder="e.g. Reception PC, Accountant-01"
              required
              className="w-full px-3.5 py-2.5 rounded-xl bg-black/[0.03] dark:bg-zinc-800/80 border border-black/10 dark:border-white/10 text-xs text-zinc-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-pulse-blue/50 transition font-medium"
            />
            <p className="text-[11px] text-zinc-500 dark:text-zinc-400 mt-1">
              Hardware hostname: <span className="font-mono font-semibold">{device.hostname}</span>
            </p>
          </div>

          <div>
            <label className="block text-xs font-semibold text-zinc-700 dark:text-zinc-300 mb-1.5 flex items-center justify-between">
              <span>Assigned Staff Member</span>
              {loadingUsers && <span className="text-[10px] text-zinc-400">Loading staff...</span>}
            </label>
            <div className="relative">
              <select
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                disabled={loadingUsers}
                className="w-full px-3.5 py-2.5 rounded-xl bg-black/[0.03] dark:bg-zinc-800/80 border border-black/10 dark:border-white/10 text-xs text-zinc-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-pulse-blue/50 transition font-medium appearance-none cursor-pointer"
              >
                <option value="">Unassigned (No staff attached)</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.fullName} {u.department ? `(${u.department})` : ''} — {u.email}
                  </option>
                ))}
              </select>
            </div>
            <p className="text-[11px] text-zinc-500 dark:text-zinc-400 mt-1">
              Workstation inactivity metrics and alerts will be attributed to this staff member.
            </p>
          </div>

          <div className="pt-2 flex items-center justify-end gap-2.5">
            <button
              type="button"
              onClick={() => {
                sounds.playTapSound();
                onClose();
              }}
              className="px-4 py-2 text-xs font-semibold rounded-xl bg-zinc-100 hover:bg-zinc-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-zinc-700 dark:text-zinc-300 transition ios-press"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-bold rounded-xl bg-pulse-blue hover:bg-pulse-blueHover text-white shadow-md shadow-pulse-blue/20 transition ios-press disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              <span>{saving ? 'Saving...' : 'Save Changes'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
